import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, map, pairwise, ReplaySubject, switchMap, take } from 'rxjs';
import { BildungsangebotTerminDetail } from '../../model';
import { UrlService } from './url.service';

/**
 * Hält den Zustand, den Trefferliste und Detailseite gemeinsam brauchen:
 * den gewählten Termin, die Menge der aufgeklappten Angebotskarten und den Anker,
 * auf den nach der Rückkehr von der Detailseite gesprungen wird. Als Rootservice
 * registriert, damit dieser Zustand den Seitenwechsel überlebt.  
 */

@Injectable({
  providedIn: 'root'
})
export class CommonStateService {
  private readonly urlService = inject(UrlService);

  private readonly router = inject(Router);

  private readonly _selectedTermin$ = new ReplaySubject<BildungsangebotTerminDetail | null>();

  private readonly _expandedAngebotIds$ = new BehaviorSubject<number[]>([]);

  private anchorAngebotId: number | null = null;

  public selectedTermin$ = this._selectedTermin$.asObservable();

  public expandedAngebotIds$ = this._expandedAngebotIds$.asObservable();

  // Fehlender Parameter ergibt 0, nicht null, deshalb prüfen die Verwender mit if (angebotId)
  // und nicht gegen null
  public selectedAngebotId$ = this.urlService
    .getQueryParameterValue(UrlService.SELECTED_ANGEBOT)
    .pipe(map(Number));

  constructor() {
    // Der Parameter wird nur beim Eintritt übernommen. Spätere Änderungen stammen vom
    // Auf- und Zuklappen selbst und dürfen nicht erneut aufklappen
    this.selectedAngebotId$.pipe(take(1)).subscribe((angebotId) => {
      if (angebotId) {
        this.anchorAngebotId = angebotId;
        this._expandedAngebotIds$.next([angebotId]);
      }
    });

    // Rücksprung von Detailseite setzt den Anker neu
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        pairwise(),
        filter(([vorher]) => vorher.urlAfterRedirects.startsWith('/termin/')),
        switchMap(() => this.selectedAngebotId$.pipe(take(1)))
      )
      .subscribe((angebotId) => {
        if (angebotId) {
          this.anchorAngebotId = angebotId;
        }
      });
  }

  // Liefert nur beim ersten Abruf true. Danach ist der Anker verbraucht
  public consumeAnchorScroll(angebotId: number): boolean {
    if (this.anchorAngebotId !== angebotId) {
      return false;
    }
    this.anchorAngebotId = null;
    return true;
  }

  public setSelectedTermin(termindId: BildungsangebotTerminDetail | null): void {
    this._selectedTermin$.next(termindId);
  }

  public setSelectedAngebotId(angebotId: number | null): void {
    this.urlService.updateUrlQueryParams({
      [UrlService.SELECTED_ANGEBOT]: angebotId
    });
  }

  // Der Adressparameter wird ohne Verlaufseintrag mitgeführt, sonst liefe der Zurück-Button
  // durch jedes Auf- und Zuklappen. Beim Zuklappen übernimmt die letzte noch offene Karte.
  public toggleExpandedAngebotId(angebotId: number): void {
    const expanded = this._expandedAngebotIds$.getValue();
    if (expanded.includes(angebotId)) {
      const remaining = expanded.filter((id) => id !== angebotId);
      this._expandedAngebotIds$.next(remaining);
      this.setSelectedAngebotIdWithoutHistoryPush(remaining[remaining.length - 1] ?? null);
    } else {
      this._expandedAngebotIds$.next([...expanded, angebotId]);
      this.setSelectedAngebotIdWithoutHistoryPush(angebotId);
    }
  }

  private setSelectedAngebotIdWithoutHistoryPush(angebotId: number | null): void {
    this.urlService.updateUrlQueryParamsWithoutHistoryPush({
      [UrlService.SELECTED_ANGEBOT]: angebotId
    });
  }
}
